import {
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@repo/ui";
import { Link, useNavigate } from "react-router-dom";
import { FaChrome } from "react-icons/fa";
import { BiUser } from "react-icons/bi";
import { MdEmail } from "react-icons/md";
import { useContext, useMemo, useRef } from "react";
import { Copy, Download, Upload } from "lucide-react";
import { useGetParentTicketList } from "pages/tickets/utils";
import { StatusContext } from "pages/tickets/constants";
import sweetAlert from "components/sweet-alert";
import { StatusBar } from "./status-bar";
import { useUser } from "hooks/use-user";
import { PATHS } from "routes/paths";

export function TicketDetailsHeader({
  ticket = {},
  canCopy = false,
  canUpload = false,
  canDownloadTicket = false,
  canSelfAssign = false,
  isFormDisabled = false,
  isDirty = false,
  handleFileChange = () => {},
  onSave = () => {},
  onReset = () => {},
  onTicketDownload = () => {},
  onSelfAssign = () => {},
}) {
  const user = useUser();
  const isCallCenterAgent = user.Roles.includes("Call Center Agent");
  const isCSMAgent = user.Roles.includes("CSM Agent");
  const { status } = useContext(StatusContext);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const getHomeURL = useGetParentTicketList(status);
  const onUpload = () => {
    fileInputRef.current.click();
  };
  const copyTicket = async () => {
    const data = ticket;
    const textToCopy = `
    Ticket Number: ${data.caseNo},
    Requested By: ${data.reportedBy},
    Requester Email ID: ${data.requesterEmail},
    Requester Contact No.: ${data.requesterContactNo},
    Priority: ${data.priority},
    Region: ${data.region},
    Company Name: ${data.companyName},
    Description: ${data.remarks}
    `;

    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      sweetAlert({
        title: "Operation Failed",
        text: "Can not copy ticket details",
        icon: "error",
      });
    }
  };

  return (
    <>
      <Breadcrumb className="mt-5 text-black">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link to={getHomeURL} className="text-black">
                Tickets
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-black" />

          <BreadcrumbItem>
            <BreadcrumbPage className="text-black">
              {ticket.caseNo || "Case Number"}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-row justify-end w-full gap-2 items-center">
        <div
          title={`Source: ${ticket.creationChannel}`}
          style={{ background: "white" }}
        >
          <div>
            <StatusIcon creationChannel={ticket.creationChannel} />
          </div>
        </div>
        {canCopy ? (
          <Button
            variant="secondary"
            onClick={copyTicket}
            type="button"
            title="Copy Ticket"
          >
            <Copy />
          </Button>
        ) : null}
        {canUpload && (isCallCenterAgent || isCSMAgent) ? (
          <>
            <Button variant="secondary" onClick={onUpload} title="Upload File">
              <Upload />
            </Button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </>
        ) : null}

        {canDownloadTicket ? (
          <Button
            variant="secondary"
            onClick={onTicketDownload}
            type="button"
            title="Download Ticket"
          >
            <Download />
          </Button>
        ) : null}

        <Button
          variant="secondary"
          disabled={!isDirty}
          onClick={onSave}
          type="submit"
        >
          Update
        </Button>
        {canSelfAssign ? (
          <Button variant="secondary" onClick={onSelfAssign} type="button">
            Self Assign
          </Button>
        ) : null}
        <Button
          variant="secondary"
          disabled={isFormDisabled || !isDirty}
          onClick={onReset}
        >
          Reset
        </Button>

        <Button
          variant="secondary"
          onClick={() => {
            navigate(getHomeURL);
          }}
        >
          Home
        </Button>
      </div>
      <StatusBar ticketStatus={ticket.status} />
    </>
  );
}
function StatusIcon({ creationChannel }) {
  if (creationChannel === "Web") {
    return (
      <FaChrome className="text-secondary cursor-pointer" fontSize="20px" />
    );
  } else if (creationChannel === "Customer Portal") {
    return <BiUser className="text-secondary cursor-pointer" fontSize="20px" />;
  } else if (creationChannel === "Auto Email") {
    return (
      <MdEmail className="text-secondary cursor-pointer" fontSize="20px" />
    );
  }
}
